import React, { useEffect, useState } from 'react';
import { sendAdminEmail } from '../api.admin';
import {
  AdminNotificationSettings,
  adminGetNotificationSettings,
  adminUpdateNotificationSettings,
} from '../api.adminNotifications';

const DEFAULT_FROM = 'info@guestbook.cz';

const CARD_STYLE: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 10,
  padding: 28,
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
};

const AdminEmail: React.FC = () => {
  const [form, setForm] = useState({ from: DEFAULT_FROM, to: '', subject: '', body: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const [notif, setNotif] = useState<AdminNotificationSettings | null>(null);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifResult, setNotifResult] = useState<{ ok: boolean; message: string } | null>(null);

  const token = localStorage.getItem('token') || '';

  useEffect(() => {
    adminGetNotificationSettings(token)
      .then(setNotif)
      .catch(err => setNotifResult({
        ok: false,
        message: err?.response?.data?.message || err?.message || 'Failed to load notification settings',
      }));
  }, [token]);

  const handleNotifSave = async () => {
    if (!notif) return;
    setNotifSaving(true);
    setNotifResult(null);
    try {
      const updated = await adminUpdateNotificationSettings({
        newRegistrationEnabled: notif.newRegistrationEnabled,
        newRegistrationRecipients: notif.newRegistrationRecipients,
      }, token);
      setNotif(updated);
      setNotifResult({ ok: true, message: 'Notification settings saved.' });
    } catch (err: any) {
      setNotifResult({
        ok: false,
        message: err?.response?.data?.message || err?.message || 'Failed to save notification settings',
      });
    } finally {
      setNotifSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setResult(null);
    setSubmitting(true);
    try {
      const res = await sendAdminEmail(
        { from: form.from || DEFAULT_FROM, to: form.to, subject: form.subject, body: form.body },
        token,
      );
      setResult({ ok: res.success, message: res.message });
      if (res.success) {
        setForm(prev => ({ ...prev, to: '', subject: '', body: '' }));
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to send email';
      setResult({ ok: false, message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 680 }}>
      <h1 style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: 4, color: '#1a202c' }}>Email</h1>
      <p style={{ color: '#718096', marginBottom: 28, fontSize: '0.95rem' }}>
        Automatic admin notifications, and a form for sending a one-off plain-text email via the
        configured SMTP/SES connection.
      </p>

      {/* ── Notifications ─────────────────────────────────────── */}
      <section style={{ ...CARD_STYLE, marginBottom: 28 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1a202c' }}>Notifications</div>
          <div style={{ color: '#718096', fontSize: '0.88rem', marginTop: 4 }}>
            Events that email the admins automatically.
          </div>
        </div>

        {notif ? (
          <>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={notif.newRegistrationEnabled}
                onChange={e => {
                  setNotif({ ...notif, newRegistrationEnabled: e.target.checked });
                  setNotifResult(null);
                }}
                style={{ marginTop: 3, width: 16, height: 16, cursor: 'pointer' }}
              />
              <span>
                <span style={{ fontWeight: 600, fontSize: '0.92rem', color: '#2d3748' }}>
                  Email admins when someone registers
                </span>
                <span style={{ display: 'block', fontSize: '0.82rem', color: '#718096', marginTop: 2 }}>
                  One message per registration, with the new account's name, email and a link to it.
                </span>
              </span>
            </label>

            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 5, fontSize: '0.9rem', color: '#2d3748' }}>
                Recipients
              </label>
              <input
                type="text"
                value={notif.newRegistrationRecipients ?? ''}
                onChange={e => {
                  setNotif({ ...notif, newRegistrationRecipients: e.target.value });
                  setNotifResult(null);
                }}
                placeholder="Leave blank to notify every active admin"
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.95rem', boxSizing: 'border-box' }}
              />
              <div style={{ fontSize: '0.82rem', color: '#718096', marginTop: 4 }}>
                {notif.effectiveRecipients.length > 0
                  ? <>Currently goes to: <strong>{notif.effectiveRecipients.join(', ')}</strong></>
                  : 'Nobody would be notified — add an address, or activate an admin account.'}
              </div>
            </div>

            {notifResult && (
              <div style={{
                padding: '10px 14px',
                borderRadius: 6,
                background: notifResult.ok ? '#f0fff4' : '#fff5f5',
                border: `1px solid ${notifResult.ok ? '#68d391' : '#fc8181'}`,
                color: notifResult.ok ? '#22543d' : '#c53030',
                fontWeight: 600,
                fontSize: '0.92rem',
              }}>
                {notifResult.ok ? '✅' : '❌'} {notifResult.message}
              </div>
            )}

            <div>
              <button
                type="button"
                onClick={handleNotifSave}
                disabled={notifSaving}
                style={{
                  padding: '10px 24px',
                  background: notifSaving ? '#a0aec0' : '#3182ce',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 7,
                  cursor: notifSaving ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                }}
              >
                {notifSaving ? 'Saving…' : 'Save notifications'}
              </button>
            </div>
          </>
        ) : (
          <div style={{ color: notifResult && !notifResult.ok ? '#c53030' : '#718096', fontSize: '0.9rem' }}>
            {notifResult && !notifResult.ok ? notifResult.message : 'Loading…'}
          </div>
        )}
      </section>

      {/* ── One-off email ─────────────────────────────────────── */}
      <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#1a202c', marginBottom: 12 }}>Send Email</div>

      <form onSubmit={handleSubmit} style={CARD_STYLE}>

        {/* From */}
        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 5, fontSize: '0.9rem', color: '#2d3748' }}>
            From
          </label>
          <input
            type="email"
            name="from"
            value={form.from}
            onChange={handleChange}
            placeholder={DEFAULT_FROM}
            style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.95rem', boxSizing: 'border-box' }}
          />
          <div style={{ fontSize: '0.82rem', color: '#718096', marginTop: 4 }}>
            Leave blank to use the server default (<code>no-reply@guestbook.cz</code>).
          </div>
        </div>

        {/* To */}
        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 5, fontSize: '0.9rem', color: '#2d3748' }}>
            To <span style={{ color: '#e53e3e' }}>*</span>
          </label>
          <input
            type="email"
            name="to"
            value={form.to}
            onChange={handleChange}
            required
            placeholder="recipient@example.com"
            style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.95rem', boxSizing: 'border-box' }}
          />
        </div>

        {/* Subject */}
        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 5, fontSize: '0.9rem', color: '#2d3748' }}>
            Subject <span style={{ color: '#e53e3e' }}>*</span>
          </label>
          <input
            type="text"
            name="subject"
            value={form.subject}
            onChange={handleChange}
            required
            placeholder="Email subject"
            style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.95rem', boxSizing: 'border-box' }}
          />
        </div>

        {/* Body */}
        <div>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 5, fontSize: '0.9rem', color: '#2d3748' }}>
            Message <span style={{ color: '#e53e3e' }}>*</span>
          </label>
          <textarea
            name="body"
            value={form.body}
            onChange={handleChange}
            required
            rows={10}
            placeholder="Write your message here…"
            style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.95rem', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
          />
          <div style={{ fontSize: '0.82rem', color: '#718096', marginTop: 4 }}>
            Plain text only. Line breaks are preserved.
          </div>
        </div>

        {/* Result */}
        {result && (
          <div style={{
            padding: '10px 14px',
            borderRadius: 6,
            background: result.ok ? '#f0fff4' : '#fff5f5',
            border: `1px solid ${result.ok ? '#68d391' : '#fc8181'}`,
            color: result.ok ? '#22543d' : '#c53030',
            fontWeight: 600,
            fontSize: '0.92rem',
          }}>
            {result.ok ? '✅' : '❌'} {result.message}
          </div>
        )}

        {/* Submit */}
        <div>
          <button
            type="submit"
            disabled={submitting}
            style={{
              padding: '10px 24px',
              background: submitting ? '#a0aec0' : '#3182ce',
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontWeight: 600,
              fontSize: '0.95rem',
            }}
          >
            {submitting ? 'Sending…' : 'Send Email'}
          </button>
        </div>

      </form>
    </div>
  );
};

export default AdminEmail;
